import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams, useLocation } from 'react-router-dom'
import { isNil } from 'lodash'
import { useIdentity } from 'pages/Identity/Login'
import { Grid, Typography, FormControlLabel, Switch, Select, MenuItem, FormControl, InputLabel, Box, Alert, Snackbar, Menu, MenuItem as MenuItemComponent, useTheme, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import UploadIcon from '@mui/icons-material/Upload'
import { List } from 'components/Listing/List'
import Loader from 'components/Loader'
import { fetchCandidateJobs, fetchHospitalJobs } from 'src/redux/jobs'
import { fetchJobFilterValues } from 'src/redux/filters'
import JobContent from './JobContent'
import { useJobs } from './useJobs'
import JobDrawer from 'components/Drawers/JobDrawer/JobDrawer'
import SmartUploadDrawer from 'components/Drawers/SmartUploadDrawer/SmartUploadDrawer'
import { Filters } from '../../components/Filters'
import { FilterType } from '../../components/Filters/filterDefinitions'
import { candidateTypeDefinitions } from 'components/Filters/candidateTypeDefinitions'
import { filterDefinitions } from 'components/Filters/filterDefinitions'
import { fetchUpdateCandidateJob } from 'src/redux/candidateJob'
import { applyFilters } from 'components/Filters/helpers'
import { get as getConfig } from 'config'

// AI Components
import AIJobInterface from './AI/AIJobInterface'
import { updateAiJobFavorite } from '../../redux/aiJobSearch'

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase': {
    color: theme.palette.grey[400],
    '&.Mui-checked': {
      color: theme.palette.error.main,
    },
    '&.Mui-checked + .MuiSwitch-track': {
      backgroundColor: theme.palette.error.light,
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: theme.palette.grey[300],
  },
}))

const AIStyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase': {
    color: theme.palette.primary.main,
    '&.Mui-checked': {
      color: theme.palette.secondary.main,
    },
    '&.Mui-checked + .MuiSwitch-track': {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: theme.palette.grey[300],
  },
}))

const Jobs = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down('md'))
  const signedIn = useSelector(state => state.identity.signedIn)
  
  // Config flags for AI features
  const allowAIJobSearch = getConfig('allowAIJobSearch', false)
  const allowBulkAIUpload = getConfig('allowBulkAIUpload', false)
  const candidate = useSelector(state => state.candidate)
  const {
    candidateType,
    specialties = [],
    loading: candidateLoading,
  } = candidate
  const { loading: employerUserLoading } = useSelector(state => state.employerUser)
  const loading = candidateLoading || employerUserLoading
  const { employerId, employerUserId, candidateId, userId, isEmployerAdmin } = useIdentity()
  const [activeItem, setActiveItem] = useState()
  const [page, setPage] = useState(0)
  const pageSize = 50
  const { jobsList = [], isLoadingJobs } = useSelector(state => state.jobs)
  const { isLoadingFilters } = useSelector(state => state.filters).loading
  const filterValues = useSelector(state => state.filters.jobFilterValues) || {}
  const totalJobs = useSelector(state => state.jobs.totalJobs)
  const totalPages = Math.ceil(totalJobs / pageSize)
  const dataCall = employerUserId ? fetchHospitalJobs : fetchCandidateJobs
  const [searchParams, setSearchParams] = useSearchParams()
  const candidateTypeId = searchParams.get('candidateTypeId')
  const [displayedData, setDisplayedData] = useState(jobsList)
  const [isApplyingFilters, setIsApplyingFilters] = useState(false)

  // Added state for the "Favorites Only" filter
  const [favoritesOnly, setFavoritesOnly] = useState(false)

  const [archivedFilter, setArchivedFilter] = useState('all')

  // AI-related state
  const [isAIMode, setIsAIMode] = useState(false)

  const isInitialMountRef = React.useRef(true)

  // Smart Job Upload notification state
  const [showUploadSuccess, setShowUploadSuccess] = useState(false)
  const [uploadedJobsCount, setUploadedJobsCount] = useState(0)

  // Upload menu state
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState(null)
  
  // Job drawer state (for manual upload)
  const { drawerJobOpen, setDrawerJobOpen } = useJobs()
  
  // Smart upload drawer state
  const [smartUploadDrawerOpen, setSmartUploadDrawerOpen] = useState(false)

  // Resizable panel state
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    const saved = localStorage.getItem('jobsLeftPanelWidth')
    // Default 30% of typical viewport width (assuming ~1400px width = ~420px)
    return saved ? parseInt(saved) : 420
  })
  const [isResizing, setIsResizing] = useState(false)

  const startResize = (e) => {
    if (mobile) return
    setIsResizing(true)
    e.preventDefault()
  }

  useEffect(() => {
    if (!isResizing) return
    
    const handleMouseMove = (e) => {
      requestAnimationFrame(() => {
        const container = document.getElementById('jobs-container')
        if (!container) return
        
        const containerRect = container.getBoundingClientRect()
        const newWidth = e.clientX - containerRect.left
        
        if (newWidth >= 250 && newWidth <= 1000) {
          setLeftPanelWidth(newWidth)
        }
      })
    }
    
    const stopResize = () => {
      setIsResizing(false)
      localStorage.setItem('jobsLeftPanelWidth', leftPanelWidth.toString())
    }
    
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', stopResize)
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', stopResize)
    }
  }, [isResizing, leftPanelWidth, mobile])

  const handleListItemClick = currId => {
    const activeJob = !isNil(currId)
      ? displayedData.find(j => `${j.id}` === `${currId}`)
      : displayedData.length > 0
        ? displayedData[0]
        : null
    setActiveItem(activeJob)
  }

  useEffect(() => {
    if (!signedIn) {
      dispatch(dataCall({ page, pageSize }))
    }
  }, [signedIn, dispatch, dataCall, page, pageSize])

  useEffect(() => {
    if (!mobile && jobsList.length > 0) {
      setActiveItem(jobsList[0])
    } else if (mobile) {
      setActiveItem(null)
    }
  }, [jobsList, mobile])

  useEffect(() => {
    if (location.state?.newlyUploaded) {
      setUploadedJobsCount(location.state.newlyUploaded)
      setShowUploadSuccess(true)
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  useEffect(() => {
    if (candidateType && candidateType.id && searchParams.size === 0) {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('candidateTypeId', candidateType.id)
      const candidateTypeDefinition = candidateTypeDefinitions.find(
        def => def.id === candidateType.id,
      )
      const specialtyFilterDefinition = filterDefinitions.find(
        filter => filter.name === 'specialty',
      )
      if (candidateTypeDefinition?.useCandidateSpecialty === true) {
        if (specialties.length > 0) {
          newParams.set(specialtyFilterDefinition.queryStringKey, specialties[0].id)
        } else {
          newParams.delete(specialtyFilterDefinition.queryStringKey)
        }
      } else {
        newParams.delete(specialtyFilterDefinition.queryStringKey)
      }
      setSearchParams(newParams)
    }
  }, [candidateType, specialties])

  useEffect(() => {
    if (candidateTypeId) {
      const specialtyIdsParam = searchParams.get('specialtyIds')
      dispatch(
        fetchJobFilterValues({
          employerId: employerId,
          candidateTypeId: searchParams.get('candidateTypeId'),
          specialtyIds: specialtyIdsParam ? specialtyIdsParam.split('|') : [],
        }),
      )
    }
    else {
      dispatch(
        fetchJobFilterValues({
          employerId: employerId,
        }),
      )
    }
  }, [candidateTypeId, employerId])

  // Update displayedData when jobsList changes
  useEffect(() => {
    setDisplayedData(jobsList)
  }, [jobsList])

  // Handle URL parameter changes
  useEffect(() => {
    if (searchParams.size > 0 && !isAIMode) {
      setIsApplyingFilters(true)
      applyFilters({
        candidateId,
        employerUserId,
        searchParams,
        dispatch,
        getData: dataCall,
        page,
        pageSize,
        employerId,
        isEmployerAdmin,
        userId,
      })
      // Set a timeout to turn off the loading state after a short delay
      const timer = setTimeout(() => {
        setIsApplyingFilters(false)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [searchParams, isAIMode])

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      return
    }

    if (!isAIMode) {
      const updatedSearchParams = new URLSearchParams(searchParams)
      if (favoritesOnly) {
        updatedSearchParams.set('favorite', 'true')
      } else {
        updatedSearchParams.delete('favorite')
      }

      if (archivedFilter !== 'all') {
        updatedSearchParams.set('archived', archivedFilter)
      } else {
        updatedSearchParams.delete('archived')
      }

      setIsApplyingFilters(true)
      applyFilters({
        candidateId,
        employerUserId,
        searchParams: updatedSearchParams,
        dispatch,
        getData: dataCall,
        page,
        pageSize,
        employerId,
        isEmployerAdmin,
        userId,
      })
      // Set a timeout to turn off the loading state after a short delay
      const timer = setTimeout(() => {
        setIsApplyingFilters(false)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [favoritesOnly, archivedFilter, isAIMode])

  const toggleFavoritesFilter = () => {
    setFavoritesOnly(!favoritesOnly)
  }

  const handleArchivedFilterChange = (event) => {
    setArchivedFilter(event.target.value)
  }

  const toggleAIMode = () => {
    setIsAIMode(!isAIMode)
  }

  // Upload functionality handlers
  const handleUploadMenuClick = (event) => {
    setUploadMenuAnchor(event.currentTarget)
  }

  const handleUploadMenuClose = () => {
    setUploadMenuAnchor(null)
  }

  const handleSmartUpload = () => {
    handleUploadMenuClose()
    setSmartUploadDrawerOpen(true)
  }

  const handleManualUpload = () => {
    handleUploadMenuClose()
    setDrawerJobOpen(true)
  }

  const leftPanelHeader = 'Results: ' + `${totalJobs ? totalJobs.toLocaleString() : 0}` + ' Jobs'

  const applyJobFilters = ({ searchParams }) => {
    if (!isAIMode) {
      applyFilters({
        candidateId,
        employerUserId,
        searchParams,
        dispatch,
        getData: dataCall,
        page,
        pageSize,
        employerId,
        isEmployerAdmin,
        userId,
      })
    }
  }

  const onFavoriteClick = ({ favorite, jobId }) => {
    if (isAIMode) {
      // For AI mode, update AI job data in Redux
      dispatch(updateAiJobFavorite({ jobId, favorite }))
    } else {
      // For regular mode, update displayedData
      const updatedData = displayedData.map(item =>
        item.id === jobId
          ? {
              ...item,
              candidateJob: {
                ...item.candidateJob,
                favorite,
              },
            }
          : item,
      )

      setDisplayedData(updatedData)

      if (activeItem && activeItem.id === jobId) {
        const updatedActiveItem = {
          ...activeItem,
          candidateJob: {
            ...activeItem.candidateJob,
            favorite,
          },
        }
        setActiveItem(updatedActiveItem)
      }
    }

    dispatch(fetchUpdateCandidateJob({ candidateId, jobId, favorite }))
  }

  return (
    <>
      <Grid 
        container 
        justifyContent='center'
        className="ai-smart-search-container"
        sx={{
          marginRight: 0,
          transition: 'margin-right 0.3s ease',
        }}
      >
        <Grid item xs={12}>
          {!isAIMode && (
            <Filters
              filterType={FilterType.JOB}
              filterValues={filterValues}
              listType='job'
              loading={isLoadingFilters}
              applyFilters={applyJobFilters}
            />
          )}
        </Grid>
        
        {/* AI Toggle Banner */}
        {signedIn && !employerUserId && allowAIJobSearch && (
          <Grid item xs={12}>
            <Box
              onClick={toggleAIMode}
              sx={{
                background: isAIMode 
                  ? 'linear-gradient(135deg, rgba(14, 124, 123, 0.15), rgba(14, 124, 123, 0.08))'
                  : 'linear-gradient(135deg, rgba(248, 249, 250, 0.9), rgba(255, 255, 255, 0.7))',
                borderRadius: 3,
                padding: '14px 24px',
                margin: '12px 0',
                cursor: 'pointer',
                border: isAIMode 
                  ? '2px solid rgba(14, 124, 123, 0.5)' 
                  : '2px solid rgba(14, 124, 123, 0.2)',
                boxShadow: isAIMode 
                  ? '0 4px 12px rgba(14, 124, 123, 0.15)' 
                  : '0 2px 8px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: isAIMode 
                    ? `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.success.main})`
                    : 'transparent',
                  opacity: isAIMode ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover': {
                  backgroundColor: isAIMode 
                    ? `${theme.palette.secondary.main}33`
                    : `${theme.palette.secondary.main}0D`,
                  borderColor: `${theme.palette.secondary.main}99`,
                  boxShadow: isAIMode 
                    ? `0 6px 20px ${theme.palette.secondary.main}33` 
                    : '0 4px 12px rgba(0, 0, 0, 0.12)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <AutoAwesomeIcon 
                  sx={{ 
                    fontSize: '1.4rem',
                    color: theme.palette.secondary.main,
                    opacity: isAIMode ? 1 : 0.8,
                  }} 
                />
                <Box>
                  <Typography 
                    variant='body2' 
                    sx={{ 
                      color: isAIMode ? theme.palette.secondary.main : theme.palette.grey[600],
                      fontSize: '0.95rem',
                      lineHeight: 1.2,
                      fontWeight: isAIMode ? 'bold' : 'medium',
                      marginBottom: 0.3,
                    }}
                  >
                    {isAIMode 
                      ? 'Smart Search Mode Active'
                      : 'Try Smart Search with AI'
                    }
                  </Typography>
                  <Typography 
                    variant='caption' 
                    sx={{ 
                      color: isAIMode ? theme.palette.grey[700] : theme.palette.grey[600],
                      fontSize: '0.8rem',
                      lineHeight: 1.2,
                      fontWeight: 'normal',
                    }}
                  >
                    {isAIMode 
                      ? 'AI-powered intelligent matching • Click to return to traditional search'
                      : 'Search naturally: "Emergency medicine jobs in California"'
                    }
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                padding: '4px 8px',
                borderRadius: 2,
                backgroundColor: theme.palette.action.selected,
                border: `1px solid ${theme.palette.secondary.main}33`,
              }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.75rem',
                    fontWeight: 'medium',
                    color: isAIMode ? theme.palette.secondary.main : theme.palette.grey[600],
                    transition: 'color 0.3s ease',
                  }}
                >
                  {isAIMode ? 'ON' : 'OFF'}
                </Typography>
                <AIStyledSwitch
                  checked={isAIMode}
                  onChange={(e) => e.stopPropagation()}
                  size="medium"
                  sx={{
                    '& .MuiSwitch-switchBase': {
                      '&.Mui-checked': {
                        '& + .MuiSwitch-track': {
                          opacity: 1,
                        },
                      },
                    },
                    '& .MuiSwitch-track': {
                      opacity: 0.7,
                    },
                  }}
                />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    color: theme.palette.secondary.main,
                    opacity: 0.8,
                  }}
                >
                  TOGGLE
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}

        {(isLoadingJobs || loading || isApplyingFilters) && !isAIMode ? (
          <Loader />
        ) : isAIMode ? (
          <AIJobInterface 
            onFavoriteClick={onFavoriteClick}
            favoritesOnly={favoritesOnly}
            toggleFavoritesFilter={toggleFavoritesFilter}
            signedIn={signedIn}
            employerUserId={employerUserId}
          />
        ) : (
          <Box id="jobs-container" sx={{ 
            display: 'flex',
            width: '100%',
            maxWidth: '100vw',
            overflow: 'hidden',
            px: { xs: 0, md: 4 },
          }}>
            <Grid
              container
              item
              xs={12}
              direction='column'
              alignContent='flex-start'
              sx={{ 
                height: 'calc(100vh - 64px)',
                minHeight: 'calc(100vh - 64px)',
                flex: { xs: '1', md: `0 0 ${leftPanelWidth}px` },
                width: { xs: '100%', md: 'auto' },
                overflow: 'hidden',
                px: { xs: 0 },
                '& .MuiBox-root': {
                  px: { xs: 0, md: 3 }
                }
              }}
            >
              {/* Consistent Header Structure */}
              <Grid
                container
                item
                alignItems='center'
                justifyContent='space-between'
                sx={{ 
                  width: '100%', 
                  overflow: 'hidden', 
                  px: { xs: 2 },
                  py: 2, // Consistent padding
                  borderBottom: '1px solid rgba(0, 0, 0, 0.12)', // Visual separator under Results section
                  backgroundColor: 'white', // Consistent background
                  '& .MuiBox-root': {
                    px: { xs: 0, md: 3 }
                  }
                }}
              >
                <Grid container item xs={12} alignItems="center" justifyContent="space-between">
                  <Grid item>
                    <Typography variant='h6' sx={{ 
                      fontWeight: 'bold',
                    }}>
                      {leftPanelHeader}
                    </Typography>

                  </Grid>
                  <Grid container item xs="auto" spacing={2} justifyContent="flex-end">
                    {signedIn && employerUserId && (
                      <Grid item>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <InputLabel sx={{ fontSize: '0.875rem' }}>Status</InputLabel>
                          <Select
                            value={archivedFilter}
                            onChange={handleArchivedFilterChange}
                            label="Status"
                            sx={{ 
                              fontSize: '0.875rem',
                              height: '32px'
                            }}
                          >
                            <MenuItem value="all" sx={{ fontSize: '0.875rem' }}>All Jobs</MenuItem>
                            <MenuItem value="true" sx={{ fontSize: '0.875rem' }}>Archived</MenuItem>
                            <MenuItem value="false" sx={{ fontSize: '0.875rem' }}>Active</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    {signedIn && !employerUserId && (
                      <Grid item>
                        <FormControlLabel
                          control={
                            <StyledSwitch
                              checked={favoritesOnly}
                              onChange={toggleFavoritesFilter}
                              icon={<FavoriteBorderIcon />}
                              checkedIcon={<FavoriteIcon />}
                            />
                          }
                          label='Favorites Only'
                          labelPlacement='start'
                          sx={{ marginRight: 0 }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              
              {/* Content Area - Regular Job List */}
              <Grid container alignItems='flex-start' sx={{ 
                maxHeight: 'calc(100vh - 64px)', 
                overflow: 'auto',
                flex: 1,
                width: '100%'
              }}>
                {/* Regular Job List */}
                <List
                  setPage={setPage}
                  page={page}
                  data={displayedData}
                  listType='job'
                  handleListItemClick={handleListItemClick}
                  dataTotal={totalJobs}
                  totalPages={totalPages}
                  signedIn={signedIn}
                  setDisplayedData={setDisplayedData}
                  onFavoriteClick={onFavoriteClick}
                  setActiveItem={setActiveItem}
                />
              </Grid>
            </Grid>
            
            {/* Resizable Divider */}
            <Box
              onMouseDown={startResize}
              sx={{
                display: { xs: 'none', md: 'block' },
                flexShrink: 0,
                width: '4px',
                cursor: 'col-resize',
                backgroundColor: 'transparent',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '1px',
                  height: '100%',
                  backgroundColor: theme.palette.divider,
                },
                '&:hover, &:active': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            />
            
            {/* Right Panel */}
            <Grid
              container
              item
              xs={12}
              direction='column'
              sx={{ 
                height: 'calc(100vh - 64px)',
                minHeight: 'calc(100vh - 64px)',
                flex: { xs: '1', md: '1' },
                pl: { md: 3 },
                overflow: 'auto',
                display: { xs: 'none', md: 'flex' },
              }}
            >
              <JobContent
                item={activeItem}
                signedIn={signedIn}
                employerUserId={employerUserId}
                onFavoriteClick={onFavoriteClick}
                jobs={jobsList}
                activeItem={activeItem}
                onUploadMenuClick={handleUploadMenuClick}
                uploadMenuAnchor={uploadMenuAnchor}
                onUploadMenuClose={handleUploadMenuClose}
                onSmartUpload={handleSmartUpload}
                onManualUpload={handleManualUpload}
              />
            </Grid>
          </Box>
        )}
      </Grid>
      
      {/* Upload method selection menu */}
      <Menu
        anchorEl={uploadMenuAnchor}
        open={Boolean(uploadMenuAnchor)}
        onClose={handleUploadMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {allowBulkAIUpload && (
          <MenuItemComponent onClick={handleSmartUpload}>
            <SmartToyIcon sx={{ mr: 2 }} />
            Smart Upload (AI-Powered)
          </MenuItemComponent>
        )}
        <MenuItemComponent onClick={handleManualUpload}>
          <UploadIcon sx={{ mr: 2 }} />
          Manual Upload
        </MenuItemComponent>
      </Menu>

      {/* Manual upload drawer */}
      <JobDrawer
        open={drawerJobOpen}
        onClose={() => setDrawerJobOpen(false)}
        isEdit={false}
        setOpenDrawer={setDrawerJobOpen}
      />

      {/* Smart upload drawer */}
      <SmartUploadDrawer
        open={smartUploadDrawerOpen}
        onClose={() => setSmartUploadDrawerOpen(false)}
      />

      {/* Success notification for newly uploaded jobs */}
      <Snackbar
        open={showUploadSuccess}
        autoHideDuration={6000}
        onClose={() => setShowUploadSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowUploadSuccess(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Successfully uploaded {uploadedJobsCount} job{uploadedJobsCount !== 1 ? 's' : ''}! 
          Your new listings are now live.
        </Alert>
      </Snackbar>
    </>
  )
}

export default Jobs
